import { 
    Group, Color, PlaneGeometry, TextureLoader, SRGBColorSpace, 
    ShaderMaterial, DoubleSide, FrontSide, InstancedMesh, 
    InstancedBufferAttribute, Mesh, MeshBasicMaterial 
} from "three";
import {
    vertexShader,
    fragmentShader,
} from "~/assets/shaders/about/atlasPlanes";
import colors from "./colors.js";
import { random } from "~/lib/utils.js";

// abstract class
export default class AtlasPlanes extends Group {
    maxPlaneCount = 3000;
    planeCount = 10;
    geometry = null;
    texturePath = "";
    shapeCount = 1;
    doublePlane = false;
    instancing = false;
    planes = [];
    color = new Color(colors.orange);

    constructor() {
        super();

        this.preInit();
        this._initGeometry();
        this._initTexture();
        this._initPlanes(this.planeCount);

        if (this.instancing) {
            this._initInstancing();
        }
    }

    preInit() {}

    _initGeometry() {
        this.geometry = new PlaneGeometry(1, 1);
    }

    _initTexture() {
        const textureLoader = new TextureLoader();
        this.texture = textureLoader.load(this.texturePath);
        this.texture.colorSpace = SRGBColorSpace;
    }

    _initPlanes(count) {
        for (let i = 0; i < count; i++) {
            const plane = this._initPlane();
            this.add(plane);
            this.planes.push(plane);

            if (this.instancing) {
                plane.visible = false;
            }
        }
    }

    _initInstancing() {
        const material = new ShaderMaterial({
            uniforms: {
                uMap: { value: this.texture },
                uShapeCount: { value: this.shapeCount },
                uColor: { value: this.color },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            side: this.doublePlane ? DoubleSide : FrontSide,
        });

        this.instancedMesh = new InstancedMesh(
            this.geometry,
            material,
            this.maxPlaneCount,
        );
        this.instancedMesh.renderOrder = -1;
        this.instancedMesh.frustumCulled = false;
        this.instancedMesh.count = this.planeCount;

        this.shapeIndexArray = new Float32Array(this.maxPlaneCount);
        this.shapeIndexAttribute = new InstancedBufferAttribute(
            this.shapeIndexArray,
            1,
        );
        this.geometry.setAttribute("shapeIndex", this.shapeIndexAttribute);

        this.colorArray = new Float32Array(this.maxPlaneCount * 3);
        this.colorAttribute = new InstancedBufferAttribute(
            this.colorArray,
            3,
        );
        this.geometry.setAttribute("color", this.colorAttribute);

        this.ratioArray = new Float32Array(this.maxPlaneCount);
        this.ratioAttribute = new InstancedBufferAttribute(
            this.ratioArray,
            1,
        );
        this.geometry.setAttribute("ratio", this.ratioAttribute);

        this.add(this.instancedMesh);

        this._updateAttributes();
    }

    _updateAttributes() {
        this.planes.forEach((plane, index) => {
            plane.updateMatrix();

            this.shapeIndexArray[index] = plane.visibleInShader
                ? plane.shapeIndex
                : -1;
            this.ratioArray[index] = plane.visibleInShader
                ? plane.scale.x / plane.scale.y
                : 1;
            this.colorArray[index * 3] = plane.color.r;
            this.colorArray[index * 3 + 1] = plane.color.g;
            this.colorArray[index * 3 + 2] = plane.color.b;
            this.instancedMesh.setMatrixAt(index, plane.matrix);
        });

        this.instancedMesh.instanceMatrix.needsUpdate = true;

        this.shapeIndexAttribute.set(this.shapeIndexArray);
        this.shapeIndexAttribute.needsUpdate = true;

        this.colorAttribute.set(this.colorArray);
        this.colorAttribute.needsUpdate = true;

        this.ratioAttribute.set(this.ratioArray);
        this.ratioAttribute.needsUpdate = true;
    }

    update(value) {
        this.planes.forEach((plane) => {
            this.updatePlane(plane, value);
        });

        if (this.instancing) {
            this._updateAttributes();
        }
    }

    _initPlane() {
        const shapeIndex = ~~random(this.shapeCount);

        const offset = shapeIndex / this.shapeCount;
        const width = 1 / this.shapeCount;

        const geometry = this.geometry.clone();
        const uvs = geometry.attributes.uv;

        for (let i = 0; i < uvs.count; i++) {
            const u = uvs.getX(i);
            uvs.setX(i, u * width + offset);
        }

        const plane = new Mesh(
            geometry,
            new MeshBasicMaterial({
                map: this.texture,
                transparent: true,
                side: this.doublePlane ? DoubleSide : FrontSide,
            }),
        );

        plane.shapeIndex = shapeIndex;
        plane.visibleInShader = true;

        try {
            this.initPlane(plane);
        } catch (e) {
            // console.error("Error initializing plane:", e);
        }

        return plane;
    }

    set count(value) {
        if (this.planeCount == value) return;

        if (this.planeCount > value) {
            for (let i = this.planeCount; i > value; i--) {
                const plane = this.planes.pop();
                if (!plane) continue;

                this.remove(plane);

                // Dispose of the removed plane's resources to prevent memory leaks
                plane.geometry?.dispose?.();
                plane.material?.dispose?.();

                if (this.instancing) {
                    const index = i - 1;
                    this.shapeIndexArray[index] = -1;
                    if (plane.matrix) {
                        this.instancedMesh.setMatrixAt(index, plane.matrix);
                    }
                }
            }
        } else {
            this._initPlanes(value - this.planeCount);
        }
        this.planeCount = value;

        if (this.instancing) {
            this.instancedMesh.count = value;
            this.instancedMesh.instanceMatrix.needsUpdate = true;

            this.shapeIndexAttribute.set(this.shapeIndexArray);
            this.shapeIndexAttribute.needsUpdate = true;
        }
    }

    get count() {
        return this.planeCount;
    }

    initPlane(plane) {}

    updatePlane(plane, value) {}

    destroy() {
        this.planes.forEach((plane) => {
            plane.geometry?.dispose();
            plane.material?.dispose();
        });
        this.planes = [];

        this.geometry?.dispose();
        this.texture?.dispose();

        if (this.instancedMesh) {
            this.instancedMesh.material?.dispose();
        }

        // Clean up references
        this.geometry = null;
        this.texture = null;
    }
}
